import { supabase } from './supabaseStorage';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function functionsUrl(name: string): string {
  const base = (SUPABASE_URL || '').replace(/\/$/, '');
  return `${base}/functions/v1/${name}`;
}

let razorpayLoad: Promise<void> | null = null;

export function loadRazorpayCheckoutScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if ((window as unknown as { Razorpay?: unknown }).Razorpay) {
    return Promise.resolve();
  }
  if (!razorpayLoad) {
    razorpayLoad = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load Razorpay script'));
      document.body.append(s);
    });
  }
  return razorpayLoad;
}

type CreateSubResponse = {
  keyId: string;
  subscriptionId: string;
  userSubscriptionId?: string;
  updated?: boolean;
  checkoutRequired?: boolean;
  scheduleChangeAt?: string;
  error?: string;
  details?: string;
};

export type SubscriptionCheckoutResult = {
  /** User completed or dismissed Razorpay subscription modal */
  openedCheckout: boolean;
  /** Plan changed via API only (upgrade / scheduled downgrade) */
  planChangedWithoutCheckout?: boolean;
  scheduleChangeAt?: string;
};

export type CheckoutHandlers = { onSuccess?: (msg?: string) => void; onFailure?: (err: string) => void };

export async function startSubscriptionCheckout(
  planId: string,
  options: { name: string; description?: string; email?: string } = { name: 'Subscription' }
): Promise<SubscriptionCheckoutResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('You must be signed in to subscribe.');
  }

  const r = await fetch(functionsUrl('razorpay-create-subscription'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: ANON,
    },
    body: JSON.stringify({ planId }),
  });

  const j = (await r.json().catch(() => ({}))) as CreateSubResponse;
  if (!r.ok) {
    throw new Error(j.error || j.details || `Request failed (${r.status})`);
  }
  if (j.updated === true && j.checkoutRequired === false) {
    return { openedCheckout: false, planChangedWithoutCheckout: true, scheduleChangeAt: j.scheduleChangeAt };
  }
  if (!j.subscriptionId || !j.keyId) {
    throw new Error('Invalid response from server');
  }

  await loadRazorpayCheckoutScript();
  const Rzp = (window as unknown as { Razorpay: new (o: Record<string, unknown>) => { open: () => void } })
    .Razorpay;

  return new Promise((resolve, reject) => {
    const rzp = new Rzp({
      key: j.keyId,
      subscription_id: j.subscriptionId,
      name: options.name,
      description: options.description || '',
      prefill: options.email ? { email: options.email } : undefined,
      theme: { color: '#6366f1' },
      handler: () => {
        resolve({ openedCheckout: true });
      },
      modal: {
        ondismiss: () => {
          resolve({ openedCheckout: true });
        },
      },
    });
    try {
      rzp.open();
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

type CreateOrderResponse = {
  keyId: string;
  orderId: string;
  amount: number;
  currency: string;
  error?: string;
  details?: string;
};

export async function startCreditPackCheckout(
  packId: string,
  options: { name?: string; email?: string } = {}
): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('You must be signed in to purchase credits.');
  }

  const r = await fetch(functionsUrl('razorpay-create-credit-order'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: ANON,
    },
    body: JSON.stringify({ packId }),
  });

  const j = (await r.json().catch(() => ({}))) as CreateOrderResponse;
  if (!r.ok) {
    throw new Error(j.error || j.details || `Request failed (${r.status})`);
  }
  if (!j.orderId || !j.keyId) {
    throw new Error('Invalid response from server');
  }

  await loadRazorpayCheckoutScript();
  const Rzp = (window as unknown as { Razorpay: new (o: Record<string, unknown>) => { open: () => void } })
    .Razorpay;

  return new Promise((resolve, reject) => {
    const rzp = new Rzp({
      key: j.keyId,
      amount: j.amount,
      currency: j.currency || 'INR',
      order_id: j.orderId,
      name: options.name || 'Credit pack',
      description: 'One-time credits added to your balance',
      prefill: options.email ? { email: options.email } : undefined,
      theme: { color: '#6366f1' },
      handler: () => {
        resolve();
      },
      modal: {
        ondismiss: () => {
          resolve();
        },
      },
    });
    try {
      rzp.open();
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}
